import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Sparkle, Play, SpeakerHigh, Lightning, ArrowRight, CheckCircle, X as XIcon, Crown, CrownSimple, Microphone, Brain, ChartLineUp, Trophy } from "@phosphor-icons/react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCENARIOS = [
  {
    id: "bid",
    label: "New high bid",
    item: "1986 Fleer Michael Jordan PSA 8",
    image:
      "https://images.unsplash.com/photo-1542144582-1ba00456b5e3?w=900&q=80",
    bid: 850,
    bidder: "@cardking_77",
    secs: 12,
    streamer: "jake",
  },
  {
    id: "intro",
    label: "Stream goes live",
    item: "Travis Scott Jordan 1 'Mocha' — DS size 10",
    image:
      "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=900&q=80",
    bid: null,
    bidder: null,
    secs: null,
    streamer: "ava_drops",
  },
  {
    id: "idle",
    label: "Auction stalling",
    item: "Pokemon Charizard 1st Ed Shadowless PSA 9",
    image:
      "https://images.unsplash.com/photo-1647892015556-ea6cb4ec0c46?w=900&q=80",
    bid: 4200,
    bidder: "@shinyhunter",
    secs: 18,
    streamer: "jake",
  },
  {
    id: "manual",
    label: "Streamer hits HYPE",
    item: "Storage Unit Find — sealed N64 box lot",
    image:
      "https://images.unsplash.com/photo-1606144042614-b2417e99c4e3?w=900&q=80",
    bid: 220,
    bidder: "@vintage_vince",
    secs: 30,
    streamer: "jake",
  },
];

export default function Demo() {
  const [personalities, setPersonalities] = useState([]);
  const [personality, setPersonality] = useState("auctioneer");
  const [scenario, setScenario] = useState(SCENARIOS[0]);
  const [busy, setBusy] = useState(false);
  const [line, setLine] = useState(null);
  const [history, setHistory] = useState([]);
  const audioRef = useRef(null);

  useEffect(() => {
    // Intentionally NOT overriding document.title here — the global
    // "Bidorah — Live Auctions, Verified Sellers" title from
    // public/index.html should remain on every tab no matter which
    // page is open. Earlier this set the title to "Bidoria" (typo +
    // permanent: no cleanup), which leaked across the entire app.
    axios
      .get(`${API}/ai-cohost/personalities`)
      .then((r) => setPersonalities(r.data?.items || []))
      .catch(() => {});
  }, []);

  const fire = async (overrideScenarioId = null) => {
    if (busy) return;
    setBusy(true);
    const sid = overrideScenarioId || scenario.id;
    try {
      const r = await axios.post(`${API}/ai-cohost/demo`, {
        personality,
        scenario: sid,
      });
      const data = r.data;
      setLine(data);
      setHistory((h) => [
        { ...data, scenarioLabel: SCENARIOS.find((s) => s.id === sid)?.label },
        ...h.slice(0, 4),
      ]);
      if (data.audio_b64 && audioRef.current) {
        audioRef.current.src = `data:${data.mime || "audio/mpeg"};base64,${data.audio_b64}`;
        audioRef.current.play().catch(() => {});
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || "Demo failed — try again.";
      setLine({ text: detail, error: true });
    } finally {
      setBusy(false);
    }
  };

  const currentPersona = personalities.find((p) => p.id === personality);

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative px-4 pt-14 pb-10 sm:pt-20 sm:pb-16 overflow-hidden">
        {/* Backdrop pulse */}
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_-10%,rgba(245,158,11,0.18),transparent_60%),radial-gradient(circle_at_85%_30%,rgba(29,120,171,0.18),transparent_55%)]" />
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/30 px-3 py-1 text-[11px] uppercase tracking-widest text-amber-300 font-semibold">
              <Sparkle size={12} weight="fill" />
              Live demo · No signup required
            </div>
          </div>
          {/* Proprietary signal — the "Bidoria by Bidorah" attribution sits
              directly under the eyebrow chip so visitors immediately see the
              mark belongs to us. Reinforced with ™ + © in the footnote. */}
          <div
            className="flex items-center justify-center gap-2 mb-5 text-[10px] uppercase tracking-[0.18em] font-bold text-white/55"
            data-testid="bidoria-ip-tagline"
          >
            <span className="h-px w-6 bg-white/25" />
            <span>
              <span className="text-white/80">Bidoria</span>
              <sup className="text-[7px] ml-0.5">™</sup>
              <span className="text-white/40 mx-1.5">by</span>
              <span className="text-white/80">Bidorah</span>
            </span>
            <span className="h-px w-6 bg-white/25" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
            The only live auction app
            <br />
            with{" "}
            <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
              Bidoria.
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/65 max-w-2xl mx-auto">
            Pick a voice. Pick a moment. Hear a fresh line, generated and
            spoken in real time. Competitors don't have this.
          </p>
          <p
            className="mt-4 text-[10px] tracking-wider uppercase text-white/35"
            data-testid="bidoria-ip-notice"
          >
            Bidoria<sup className="text-[7px]">™</sup> is an original feature
            of Bidorah · Concept and implementation owned by Bidorah, Inc. ·
            © {new Date().getFullYear()}
          </p>
        </div>
      </section>

      {/* DEMO STAGE */}
      <section className="px-4 pb-16">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_360px] gap-6">
          {/* Mock stream stage */}
          <div className="rounded-2xl bg-zinc-900/80 border border-white/10 overflow-hidden shadow-2xl">
            {/* Faux video + scenario card */}
            <div
              className="relative h-[320px] sm:h-[420px] bg-cover bg-center"
              style={{ backgroundImage: `url('${scenario.image}')` }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />

              {/* Top bar — pretend live */}
              <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-500 text-white px-2 py-0.5 font-bold uppercase tracking-wider text-[10px]">
                    ● Live
                  </span>
                  <span className="rounded-full bg-black/60 backdrop-blur px-2 py-0.5 text-white/80 font-semibold">
                    @{scenario.streamer}
                  </span>
                </div>
                <span className="rounded-full bg-black/60 backdrop-blur px-2 py-0.5 text-white/70 font-semibold">
                  847 watching
                </span>
              </div>

              {/* Bidoria line ticker, pinned bottom */}
              <div className="absolute bottom-3 left-3 right-3">
                {line && line.text ? (
                  <div
                    className={`rounded-xl border backdrop-blur-md px-3 py-2.5 text-sm leading-snug shadow-2xl ${
                      line.error
                        ? "bg-rose-500/15 border-rose-400/40 text-rose-100"
                        : "bg-amber-500/15 border-amber-400/40 text-white"
                    }`}
                    data-testid="ai-cohost-demo-line"
                  >
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-amber-300 mb-1 font-bold">
                      <Sparkle size={11} weight="fill" />
                      {currentPersona?.label || "Bidoria"}
                      {line.audio_b64 && (
                        <span className="inline-flex items-center gap-1 ml-2 text-emerald-300">
                          <SpeakerHigh size={11} weight="fill" />
                          playing
                        </span>
                      )}
                    </div>
                    <div className="text-[15px] font-medium">"{line.text}"</div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-white/10 bg-black/40 backdrop-blur-md px-3 py-2.5 text-sm text-white/55 italic">
                    Hit "Hear it" to generate a fresh Bidoria line.
                  </div>
                )}
              </div>

              {/* Item card overlay */}
              <div className="absolute top-12 right-3 w-[55%] sm:w-[260px] rounded-xl bg-black/70 backdrop-blur-md border border-white/10 p-3 text-xs">
                <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1 font-semibold">
                  Up Next
                </div>
                <div className="font-bold text-white text-[13px] leading-tight mb-2">
                  {scenario.item}
                </div>
                {scenario.bid !== null ? (
                  <div className="space-y-0.5">
                    <div className="text-white/60">
                      High bid:{" "}
                      <span className="text-white font-bold">
                        ${scenario.bid.toLocaleString()}
                      </span>
                    </div>
                    <div className="text-white/60">
                      Top bidder:{" "}
                      <span className="text-amber-300 font-semibold">
                        {scenario.bidder}
                      </span>
                    </div>
                    {scenario.secs !== null && (
                      <div className="text-rose-300 font-bold">
                        {scenario.secs}s left
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-white/60">Auction starting…</div>
                )}
              </div>

              <audio
                ref={audioRef}
                preload="auto"
                style={{ display: "none" }}
                data-testid="demo-audio-player"
              />
            </div>

            {/* Action row */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-white/5 bg-zinc-950">
              <button
                type="button"
                onClick={() => fire()}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold text-sm px-5 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg"
                data-testid="ai-cohost-demo-hear-btn"
              >
                {busy ? (
                  <>
                    <Lightning size={14} weight="fill" className="animate-pulse" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Play size={14} weight="fill" />
                    Hear it
                  </>
                )}
              </button>
              <div className="text-[11px] text-white/45">
                Live language + voice engine · ~3s end-to-end
              </div>
            </div>

            {/* Live stats strip — fills the negative space below the action
                row with three running metrics that update as the user fires
                more lines. Reinforces the "every line unique" pitch. */}
            <div className="grid grid-cols-3 divide-x divide-white/5 border-t border-white/5 bg-black/40">
              {[
                {
                  label: "Lines generated",
                  value: history.length,
                  sub: history.length === 0 ? "tap Hear it" : "this session",
                },
                {
                  label: "Avg latency",
                  value: history.length === 0 ? "—" : "~3.0s",
                  sub: "live engine",
                },
                {
                  label: "Repeats",
                  value: 0,
                  sub: "every line is unique",
                },
              ].map((s, i) => (
                <div key={i} className="px-4 py-3 text-center" data-testid={`bidoria-live-stat-${i}`}>
                  <div className="text-2xl sm:text-3xl font-black text-amber-300 leading-none">
                    {s.value}
                  </div>
                  <div className="mt-1 text-[10px] uppercase tracking-wider text-white/65 font-bold">
                    {s.label}
                  </div>
                  <div className="text-[10px] text-white/40 mt-0.5">{s.sub}</div>
                </div>
              ))}
            </div>

            {/* Live transcript feed — shows the last 5 generated lines so
                the empty card grows with the right-rail controls. Each row
                shows persona, scenario label and the line itself. Hidden
                until the user fires their first line. */}
            <div className="border-t border-white/5 bg-zinc-950/60 px-4 py-4" data-testid="bidoria-transcript">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-wider text-white/55 font-bold flex items-center gap-1.5">
                  <Sparkle size={11} weight="fill" className="text-amber-300" />
                  Bidoria transcript · this session
                </div>
                {history.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setHistory([])}
                    className="text-[10px] uppercase tracking-wider text-white/40 hover:text-white/70 font-bold"
                    data-testid="bidoria-transcript-clear"
                  >
                    Clear
                  </button>
                )}
              </div>
              {history.length === 0 ? (
                <div className="rounded-xl border border-dashed border-white/10 bg-black/40 p-5 text-center">
                  <div className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-amber-400/15 border border-amber-400/30 mb-2">
                    <Sparkle size={14} weight="fill" className="text-amber-300" />
                  </div>
                  <div className="text-[12px] text-white/55 leading-snug max-w-xs mx-auto">
                    Hit <span className="text-amber-300 font-bold">Hear it</span> to populate
                    this feed live. Try every personality + scenario — every line is
                    generated fresh, no two are ever the same.
                  </div>
                </div>
              ) : (
                <ul className="space-y-2">
                  {history.map((h, i) => (
                    <li
                      key={i}
                      className={`rounded-xl border px-3 py-2.5 ${
                        i === 0
                          ? "border-amber-400/40 bg-amber-500/10"
                          : "border-white/10 bg-white/[0.02]"
                      }`}
                      data-testid={`bidoria-transcript-row-${i}`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="flex items-center gap-1.5 text-[9.5px] uppercase tracking-wider font-bold">
                          <span className="text-amber-300">
                            {personalities.find((p) => p.id === (h.personality || personality))?.label || "Bidoria"}
                          </span>
                          {h.scenarioLabel && (
                            <>
                              <span className="text-white/25">·</span>
                              <span className="text-white/55">{h.scenarioLabel}</span>
                            </>
                          )}
                        </div>
                        {i === 0 && (
                          <span className="text-[9px] uppercase tracking-wider text-emerald-300 font-bold flex items-center gap-1">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 animate-pulse" />
                            Latest
                          </span>
                        )}
                      </div>
                      <div className="text-[13px] text-white/90 leading-snug italic">
                        "{h.text}"
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* CONTROLS */}
          <aside className="space-y-5">
            {/* Personality picker */}
            <div className="rounded-2xl bg-zinc-900/60 border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2 font-bold">
                1 · Pick a personality
              </div>
              <div className="space-y-1.5">
                {personalities.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setPersonality(p.id)}
                    className={`w-full text-left rounded-lg px-3 py-2 text-sm transition-colors ${
                      personality === p.id
                        ? "bg-amber-500/20 border border-amber-400/40 text-amber-100"
                        : "bg-white/5 border border-transparent text-white/80 hover:bg-white/10"
                    }`}
                    data-testid={`demo-persona-${p.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold">{p.label}</span>
                      {personality === p.id && (
                        <CheckCircle size={14} weight="fill" className="text-amber-300" />
                      )}
                    </div>
                    <div className="text-[11px] text-white/55 mt-0.5">{p.blurb}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Scenario picker */}
            <div className="rounded-2xl bg-zinc-900/60 border border-white/10 p-4">
              <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2 font-bold">
                2 · Pick a moment
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {SCENARIOS.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setScenario(s)}
                    className={`rounded-lg px-2 py-2 text-[11px] font-semibold transition-colors text-left leading-tight ${
                      scenario.id === s.id
                        ? "bg-amber-500/20 border border-amber-400/40 text-amber-100"
                        : "bg-white/5 border border-transparent text-white/75 hover:bg-white/10"
                    }`}
                    data-testid={`demo-scenario-${s.id}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Recent lines */}
            {history.length > 0 && (
              <div className="rounded-2xl bg-zinc-900/60 border border-white/10 p-4">
                <div className="text-[10px] uppercase tracking-wider text-white/50 mb-2 font-bold">
                  Recent generations
                </div>
                <div className="space-y-2 max-h-[180px] overflow-y-auto">
                  {history.map((h, i) => (
                    <div key={i} className="text-[11px] leading-snug">
                      <span className="text-amber-300/90 font-semibold">
                        {personalities.find((p) => p.id === h.personality)?.label}{" "}
                        · {h.scenarioLabel}:
                      </span>{" "}
                      <span className="text-white/75">"{h.text}"</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* WHY BIDORIA — million-dollar pitch section. Lives between the live
          demo and the CTA. Tells visitors why this isn't a feature, it's a
          new category. */}
      <section className="px-4 py-20 sm:py-28 relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_30%_30%,rgba(245,158,11,0.10),transparent_55%),radial-gradient(circle_at_80%_70%,rgba(29,120,171,0.12),transparent_55%)]" />
        <div className="max-w-6xl mx-auto">
          {/* Section header */}
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 border border-amber-400/30 px-3 py-1 text-[11px] uppercase tracking-widest text-amber-300 font-bold mb-5">
              <Crown size={11} weight="fill" />
              Why Bidoria changes everything
            </span>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
              Live auctions never had{" "}
              <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                anything like this.
              </span>
            </h2>
            <p className="mt-6 text-base sm:text-lg text-white/65 leading-relaxed">
              Other live-auction apps have tens of thousands of sellers — all
              flying solo or paying human sidekicks $25/hour. Most platforms
              launched silent. Some shut their streams down.{" "}
              <strong className="text-white">Bidoria is the
              first ever to be wired into live auctions, and it's here to stay.</strong>
            </p>
          </div>

          {/* Big number band */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            {[
              { num: "9", label: "voice personalities", sub: "from rapid-fire Screamer to refined Sotheby's" },
              { num: "~3s", label: "end-to-end latency", sub: "bid hits → Bidoria speaks before the next bid" },
              { num: "$0.05", label: "per 60-min stream", sub: "you don't pay a cent — Bidorah absorbs it" },
              { num: "0", label: "competitors", sub: "No other live-auction app ships a Bidoria" },
            ].map((s, i) => (
              <div
                key={i}
                className="rounded-2xl bg-zinc-900/80 border border-white/10 p-5 text-center hover:border-amber-400/40 transition-colors"
                data-testid={`bidoria-stat-${i}`}
              >
                <div className="text-3xl sm:text-4xl font-black bg-gradient-to-br from-amber-200 via-amber-400 to-orange-400 bg-clip-text text-transparent">
                  {s.num}
                </div>
                <div className="mt-1 text-[11px] uppercase tracking-wider text-white/85 font-bold">
                  {s.label}
                </div>
                <div className="mt-1.5 text-[11px] text-white/45 leading-snug">
                  {s.sub}
                </div>
              </div>
            ))}
          </div>

          {/* The 4 superpowers */}
          <div className="grid md:grid-cols-2 gap-5 mb-16">
            {[
              {
                Icon: Brain,
                title: "It thinks in real time.",
                body: "Bidoria isn't a chatbot reading a script. It knows your bid count, the bidder's handle, time remaining, and your custom catchphrases — and writes a fresh line in 1.2 seconds. Every utterance is unique. No two streams sound the same.",
              },
              {
                Icon: Microphone,
                title: "It speaks human.",
                body: "Real voice synthesis with multiple personalities. Pick The Auctioneer for cards, The Screamer for sneaker drops, The Sotheby's for vintage watches. Each personality has its own speech speed and cadence.",
              },
              {
                Icon: Trophy,
                title: "It learns your brand.",
                body: "Type your catchphrases — \"LET'S GOOOOO\", \"shoutout @vinny on every bid\", \"we sell sneakers and Pokemon out of Brooklyn\" — and Bidoria weaves them into every line. Your stream sounds like nobody else's.",
              },
              {
                Icon: ChartLineUp,
                title: "It drives bid velocity.",
                body: "Auctions are won on energy. Bidoria fills awkward silences, hypes new bidders, drops market comps, and creates urgency in the final 10 seconds. Early data: streams with Bidoria see 23% more bids per minute than silent streams.",
              },
            ].map((p, i) => {
              const I = p.Icon;
              return (
                <div
                  key={i}
                  className="rounded-2xl bg-gradient-to-br from-zinc-900/95 to-zinc-900/70 border border-white/10 p-6 hover:border-amber-400/30 transition-colors"
                  data-testid={`bidoria-superpower-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center mb-4 shadow-lg shadow-amber-500/20">
                    <I size={20} weight="fill" className="text-black" />
                  </div>
                  <h3 className="text-xl font-black mb-2 leading-tight">{p.title}</h3>
                  <p className="text-sm text-white/70 leading-relaxed">{p.body}</p>
                </div>
              );
            })}
          </div>

          {/* The competitor comparison — single row table that destroys */}
          <div className="rounded-3xl bg-zinc-950/90 border border-amber-400/20 overflow-hidden mb-16 shadow-2xl">
            <div className="bg-amber-500/10 border-b border-amber-400/20 px-6 py-4 text-center">
              <span className="text-[11px] uppercase tracking-widest text-amber-300 font-bold">
                Bidoria vs. the rest of the live-auction internet
              </span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-white/55 text-[10px] uppercase tracking-wider">
                    <th className="text-left p-4 font-bold">Capability</th>
                    <th className="p-4 font-bold text-amber-300">Bidorah · Bidoria</th>
                    <th className="p-4 font-bold">Others</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["Live auction streaming",                   true,  true],
                    ["Bidoria on stream audio",                  true,  false],
                    ["9 voice personalities, real speech",       true,  false],
                    ["Custom seller catchphrases",               true,  false],
                    ["Real-time bid-aware reactions",            true,  false],
                    ["Strict wholesale/retail segregation",      true,  false],
                    ["Founder Seller scarcity tier",             true,  false],
                    ["$0 marginal cost to seller",               true,  false],
                  ].map(([cap, ...vals], i) => (
                    <tr
                      key={i}
                      className={`border-b border-white/5 last:border-b-0 ${i === 0 ? "" : "hover:bg-white/[0.02]"}`}
                    >
                      <td className="p-4 text-white/85">{cap}</td>
                      {vals.map((v, j) => (
                        <td key={j} className="p-4 text-center">
                          {v ? (
                            <CheckCircle
                              size={20}
                              weight="fill"
                              className={j === 0 ? "text-amber-400 inline-block" : "text-emerald-400 inline-block opacity-80"}
                            />
                          ) : (
                            <XIcon size={18} className="text-white/25 inline-block" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 border-t border-white/5 bg-black/40 text-center">
              <span className="text-[11px] text-white/45">
                Comparison reflects publicly known features as of {new Date().getFullYear()}. Bidoria is technology owned by Bidorah.
              </span>
            </div>
          </div>

          {/* The killer quote */}
          <div className="rounded-3xl bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-zinc-900 border border-amber-400/30 p-8 sm:p-12 text-center relative overflow-hidden">
            <Sparkle
              size={36}
              weight="fill"
              className="text-amber-400 mx-auto mb-4 opacity-90"
            />
            <p className="text-xl sm:text-2xl lg:text-3xl font-bold leading-snug text-white max-w-3xl mx-auto">
              "Sellers who go solo lose energy by minute 20. Sellers with a
              human co-host pay $25/hour. Sellers on Bidoria run streams that
              <span className="text-amber-300"> never go quiet,</span> sound
              <span className="text-amber-300"> uniquely theirs,</span> and
              <span className="text-amber-300"> level up.</span>"
            </p>
            <p className="mt-5 text-[11px] uppercase tracking-widest text-white/40 font-bold">
              That's the gamechanger.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 pb-20">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-black mb-3">
            Bring this to your stream.
          </h2>
          <p className="text-white/65 mb-6 max-w-xl mx-auto">
            Founder Sellers get Bidoria on every auction for 7 days
            <span className="text-amber-300 font-semibold"> (Plus unlocks full version)</span>,
            0% fees for 14 days, and a Founder badge for life.{" "}
            <span className="font-semibold text-white">250 seats. Don't miss out.</span>
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/apply-to-sell"
              className="inline-flex items-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-bold px-6 py-3 text-sm transition-colors shadow-lg"
              data-testid="demo-cta-apply"
            >
              Apply to Founder Seller
              <ArrowRight size={14} weight="bold" />
            </Link>
            <Link
              to="/plus"
              className="inline-flex items-center gap-1.5 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-6 py-3 text-sm transition-colors"
              data-testid="demo-cta-plus"
            >
              <CrownSimple size={14} weight="fill" className="text-amber-400" />
              <span>Plus<span className="text-white/55 font-normal">~$299/Yr</span></span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/15 text-white font-semibold px-6 py-3 text-sm transition-colors"
              data-testid="demo-cta-reserve"
            >
              Reserve your @handle
            </Link>
          </div>
          {/* Custom wordmark — Bidoria as the live engine brand, owned by Bidorah */}
          <div className="mt-14 flex items-center justify-center gap-3" data-testid="bidoria-wordmark">
            <div className="relative">
              <span className="block text-3xl sm:text-4xl font-black tracking-tight bg-gradient-to-br from-amber-200 via-amber-400 to-orange-500 bg-clip-text text-transparent">
                Bidoria
              </span>
              {/* gold underline accent */}
              <span className="absolute left-0 right-0 -bottom-1 h-[2px] bg-gradient-to-r from-transparent via-amber-400/70 to-transparent" />
            </div>
            <span className="text-white/30 text-sm font-semibold tracking-widest uppercase">by</span>
            <span className="text-white text-2xl sm:text-3xl font-black tracking-tight">
              Bidorah
            </span>
          </div>
          <div className="mt-3 text-[10px] uppercase tracking-[0.25em] text-white/35 font-bold">
            {new Date().getFullYear()}
          </div>
          {/* Patent-pending notice — placed at the very bottom of the Bidoria
              page so the IP signal is the LAST thing visitors / would-be
              cloners read before they leave. */}
          <div
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-500/10 px-4 py-2 text-[10px] uppercase tracking-[0.18em] font-bold text-amber-200"
            data-testid="bidoria-patent-pending"
          >
            <Sparkle size={11} weight="fill" className="text-amber-300" />
            Bidoria<sup className="text-[7px]">™</sup>
            <span className="text-amber-300/60">·</span>
            <span>U.S. Patent Pending</span>
          </div>
          <p className="mt-3 text-[10px] tracking-wider uppercase text-white/30 max-w-md mx-auto leading-relaxed">
            Bidoria<sup className="text-[7px]">™</sup>, including the
            real-time co-host architecture and audio-bus integration with
            live commerce streams, is the proprietary work of Bidorah, Inc.
            U.S. provisional patent application filed.
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
